/**
 * LoginPage
 *
 * Centered card with:
 *  - BrightDocs branding (teal gradient "D" icon + title)
 *  - Google Sign-In button via @react-oauth/google (primary option)
 *  - Email magic-link sign-in for non-Google users (request + verify)
 *  - Restricted-access footer text
 *
 * Magic-link flow:
 *  - `?email=` prefills the email input (invite emails link that way).
 *  - `?token=` triggers POST /auth/magic-link/verify on mount; while pending a
 *    "Signing you in..." spinner replaces the form. Success stores the user via
 *    AuthProvider exactly like the Google flow; failure shows error.message
 *    with the normal form below so the user can request a fresh link.
 *
 * On successful auth the user is redirected to the appropriate dashboard.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';

import apiClient from '../api/client';
import { useAuth } from './AuthProvider';
import { tokens } from '../theme';

export default function LoginPage() {
  const { login, magicLinkLogin, error, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // --- Magic-link state ---
  // Prefill from ?email= (invite emails link that way).
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  // Guard against duplicate verify posts (tokens are single-use and React
  // StrictMode double-invokes effects in dev).
  const attemptedTokenRef = React.useRef<string | null>(null);

  const token = searchParams.get('token');

  // If already authenticated, redirect to the page they were on (when set by the
  // 401 interceptor), falling back to the role-appropriate dashboard.
  React.useEffect(() => {
    if (isAuthenticated) {
      const returnTo = searchParams.get('returnTo');
      const fallback = isAdmin ? '/admin/overview' : '/portal/home';
      // Refuse to bounce back to /login itself.
      const dest = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/login') ? returnTo : fallback;
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, searchParams]);

  // Verify a magic-link token from ?token= on mount. Success flips
  // isAuthenticated via AuthProvider, so the redirect effect above handles
  // navigation — the exact same post-login path as the Google flow.
  React.useEffect(() => {
    if (!token || attemptedTokenRef.current === token) return;
    attemptedTokenRef.current = token;
    let cancelled = false;
    setVerifying(true);
    (async () => {
      try {
        await magicLinkLogin(token);
        // Redirect handled by the isAuthenticated effect.
      } catch {
        // error.message surfaced via AuthProvider.error; drop the dead token
        // from the URL so a refresh doesn't retry it.
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('token');
              return next;
            },
            { replace: true },
          );
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, magicLinkLogin, setSearchParams]);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) return;
    setLoading(true);
    try {
      await login(response.credential);
    } catch {
      // Error surfaced via AuthProvider.error
    } finally {
      setLoading(false);
    }
  };

  // Request a sign-in link. The API always returns success (it never reveals
  // whether an account exists), so only transport failures show an error.
  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || magicLinkLoading) return;
    setMagicLinkLoading(true);
    setMagicLinkError(null);
    try {
      await apiClient.post('/auth/magic-link', { email: trimmed });
      setMagicLinkSent(true);
    } catch {
      setMagicLinkError('Could not send the sign-in link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        bgcolor: 'background.default',
        backgroundImage: `radial-gradient(circle at 12% 10%, ${tokens.accentTint} 0%, transparent 38%), radial-gradient(circle at 88% 90%, ${tokens.amberTint} 0%, transparent 40%)`,
        px: 2,
        py: 6,
      }}
    >
      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          boxShadow: '0 32px 64px -32px rgba(20, 32, 27, 0.28)',
        }}
      >
        <CardContent sx={{ p: { xs: 3.5, sm: 5 }, textAlign: 'center' }}>
          {/* Logo */}
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
            aria-hidden="true"
          >
            <Typography
              component="span"
              sx={{ color: '#ffffff', fontWeight: 800, fontSize: 22, lineHeight: 1, fontFamily: (t) => t.typography.h6.fontFamily }}
            >
              B
            </Typography>
          </Box>

          <Typography variant="eyebrow" component="div" sx={{ mb: 1 }}>
            BrightDocs by BrightRoot.ai
          </Typography>
          <Typography variant="h2" component="h1" sx={{ mb: 1 }}>
            Sign in to your workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            AI-assisted document extraction, reviewed by you.
          </Typography>

          {verifying ? (
            /* Magic-link token verification in flight */
            <Box
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
              role="status"
              aria-label="Signing you in"
            >
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Signing you in…
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {error}
                </Alert>
              )}

              {loading && (
                <Box display="flex" justifyContent="center" mb={2}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Google Sign-In — primary option */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => { /* handled by AuthProvider */ }}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  width="320"
                />
              </Box>

              <Divider sx={{ my: 2.5 }}>
                <Typography variant="eyebrow" component="span">
                  or
                </Typography>
              </Divider>

              {/* Email magic-link sign-in */}
              {magicLinkSent ? (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <MarkEmailReadOutlinedIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Check your inbox
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    If an account exists for <strong>{email.trim()}</strong>, we sent it a
                    sign-in link. The link works once and expires in 15 minutes.
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => {
                      setMagicLinkSent(false);
                      setMagicLinkError(null);
                    }}
                    sx={{ mt: 1.5, fontWeight: 700, color: 'primary.main' }}
                  >
                    Use a different email
                  </Link>
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleMagicLinkRequest}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}
                >
                  {magicLinkError && (
                    <Alert severity="error" sx={{ textAlign: 'left' }}>
                      {magicLinkError}
                    </Alert>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    inputProps={{ 'aria-label': 'Email address' }}
                    autoComplete="email"
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="outlined"
                    startIcon={
                      magicLinkLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <MailOutlineIcon fontSize="small" />
                      )
                    }
                    disabled={!email.trim() || magicLinkLoading}
                  >
                    Email me a sign-in link
                  </Button>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 320, mx: 'auto' }}>
                Access is by invitation. Sign in with Google or request a magic link to your invited email.
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
